import React, { useEffect, useRef, useState } from "react";

/**
 * A simple dropdown that doesn't close when the browser window loses focus.
 *
 * Reactstrap's <Dropdown> listens for outside-click via document events and
 * closes on focus changes — which makes the dropdown vanish whenever the user
 * alt-tabs, takes a screenshot, or even clicks the URL bar. This component
 * only closes on:
 *   - a real mousedown inside the same document (with focus), outside trigger+menu
 *   - the Escape key
 *
 * Usage:
 *   <BlurSafeDropdown
 *     trigger={(open, toggle) => <button onClick={toggle}>Open</button>}
 *     align="right"   // 'left' | 'right'
 *     menuClassName="..."
 *   >
 *     <button onClick={...}>Item 1</button>
 *     <button onClick={...}>Item 2</button>
 *   </BlurSafeDropdown>
 */
const BlurSafeDropdown = ({
  trigger,
  children,
  align = "right",
  menuClassName = "",
  containerClassName = "",
  closeOnItemClick = true,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const close = () => setOpen(false);
  const toggle = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;

    function handleMouseDown(e) {
      // Only react to *real* user clicks while the document is focused.
      // When you alt-tab, take a screenshot, or click the URL bar, the
      // document loses focus and we ignore any synthetic events.
      if (!document.hasFocus()) return;
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    }
    function handleKey(e) {
      if (e.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const onMenuClick = closeOnItemClick ? close : undefined;

  return (
    <div className={`bl-dropdown ${containerClassName}`} style={{ position: "relative", display: "inline-block" }}>
      <span ref={triggerRef}>{trigger(open, toggle)}</span>
      {open && (
        <div
          ref={menuRef}
          onClick={onMenuClick}
          className={`bl-dropdown-menu ${menuClassName}`}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            [align === "right" ? "right" : "left"]: 0,
            minWidth: 220,
            zIndex: 2050,
          }}
          role="menu"
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default BlurSafeDropdown;
